import type { AppContext } from "../app/app";
import { buildBackup } from "../data/backup";
import { el } from "./dom";

const ASKED = "persist_asked";
const BANNER_ACK = "persist_banner_ack";

export interface StorageEnv {
  supported: boolean;
  persisted: boolean;
}

// Read the live persistence state. Guards missing APIs so it is safe to call
// anywhere (and returns supported:false in a non-browser environment).
export async function readStorageEnv(): Promise<StorageEnv> {
  const s = typeof navigator !== "undefined" ? navigator.storage : undefined;
  const supported = !!(s && s.persist);
  let persisted = false;
  try {
    persisted = !!(s && s.persisted && (await s.persisted()));
  } catch {
    persisted = false;
  }
  return { supported, persisted };
}

// Show the one-time explainer-and-ask: storage is supported, not yet persisted,
// and we have not run the flow before. Pure, so it is testable headlessly.
export function shouldAskPersistence(env: StorageEnv, asked: boolean): boolean {
  return env.supported && !env.persisted && !asked;
}

// Show the honest not-persistent banner: we have run the request flow, it is
// still not persisted, and the user has not dismissed the banner. Pure.
export function shouldShowStorageBanner(
  env: StorageEnv,
  asked: boolean,
  bannerAck: boolean,
): boolean {
  return asked && !env.persisted && !bannerAck;
}

// An in-app explainer shown BEFORE the browser's own prompt (which we cannot
// control). Resolves true if the user proceeds (we then call persist()).
function showPersistExplainer(): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = el("div", { class: "consent" });
    const close = (proceed: boolean) => {
      overlay.remove();
      resolve(proceed);
    };
    overlay.append(
      el(
        "div",
        { class: "consent__card", attrs: { role: "dialog", "aria-modal": "true" } },
        el("span", { class: "label", text: "Keeping your notes" }),
        el("h2", { class: "consent__title", text: "Store your data persistently?" }),
        el("p", {
          class: "lede",
          text:
            "Trust Lens keeps your notes in this browser, with nothing on our " +
            "servers. To stop your browser clearing them automatically, we ask " +
            "your browser to store them persistently. This keeps the data on your " +
            "device; it sends nothing anywhere. Without it, your notes could be " +
            "lost if the browser reclaims space. You can export a backup at any " +
            "time.",
        }),
        el(
          "div",
          { class: "consent__actions" },
          el("button", { class: "btn btn--primary", text: "Ask my browser", on: { click: () => close(true) } }),
          el("button", { class: "btn btn--ghost", text: "Not now", on: { click: () => close(false) } }),
        ),
      ),
    );
    document.body.append(overlay);
  });
}

// Run the explainer then the persist request. Returns whether persistence is now
// granted. Does not touch the asked flag (the caller decides).
export async function runPersistRequest(): Promise<boolean> {
  if (!(await showPersistExplainer())) return false;
  try {
    return !!(navigator.storage?.persist && (await navigator.storage.persist()));
  } catch {
    return false;
  }
}

// Called at FIRST COMMITMENT (first pack seeded or first rating saved), never on
// cold first paint. Runs the explainer-and-ask once; the banner then reflects the
// outcome. Idempotent via the asked flag.
export async function maybeRequestPersistence(ctx: AppContext): Promise<void> {
  if ((await ctx.repo.getConfig(ASKED)) === "1") return;
  const env = await readStorageEnv();
  if (shouldAskPersistence(env, false)) {
    await runPersistRequest();
  }
  await ctx.repo.setConfig(ASKED, "1");
  ctx.refresh();
}

// Re-run the request from Settings ("Request again"), ignoring the asked flag.
export async function requestPersistenceAgain(ctx: AppContext): Promise<void> {
  await runPersistRequest();
  await ctx.repo.setConfig(ASKED, "1");
  ctx.refresh();
}

async function downloadBackup(ctx: AppContext): Promise<void> {
  const file = await buildBackup(ctx.repo, Date.now());
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `trust-lens-backup-${stamp}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// The honest, dismissible not-persistent banner with a working export link.
export function storageBannerEl(ctx: AppContext, onDismiss: () => void): HTMLElement {
  return el(
    "div",
    { class: "banner", attrs: { role: "status" } },
    el(
      "p",
      { class: "banner__text" },
      "Your browser has not granted persistent storage, so it may clear Trust " +
        "Lens data under storage pressure. ",
      el("button", {
        class: "link-inline",
        type: "button",
        text: "Export a backup",
        on: { click: () => void downloadBackup(ctx) },
      }),
      " to be safe.",
    ),
    el("button", {
      class: "banner__x",
      text: "×",
      attrs: { "aria-label": "Dismiss" },
      on: {
        click: async () => {
          await ctx.repo.setConfig(BANNER_ACK, "1");
          onDismiss();
        },
      },
    }),
  );
}

// The app shell uses this to decide whether to mount the banner.
export async function storageBannerVisible(ctx: AppContext): Promise<boolean> {
  const env = await readStorageEnv();
  const asked = (await ctx.repo.getConfig(ASKED)) === "1";
  const ack = (await ctx.repo.getConfig(BANNER_ACK)) === "1";
  return shouldShowStorageBanner(env, asked, ack);
}
